/**
 * Copyright (C) 2025 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

/*
  Class Description:

  Given the heading and azimuth, this class renders the look angles compass graphic with the plane icon rotated with
  respect to north the degrees of the heading and the azimuth line drawn rotated the degrees of the azimuth with respect
  to the planes rotation
 */
import React, { useCallback, useState } from 'react';
import { ThemeInterface } from '../../../../common/theme/AppThemeInterface';
import { getTerminalSatelliteList } from '../../../../../utils/TerminalUtilities';
import { SatelliteInFov } from '../../../../../shared/entities/terminal';

import isNil from 'lodash/isNil';
import { isPlatformTypeInList } from '../../../../../utils/PlatformTypeUtils';
import { TerminalPlatformsEnum } from '../../../../../shared/entities/terminalPlatform';
import { SleighIcon, SvgGroundTerminalIconPath } from '../../../TerminalIcons';
import SvgIcon from '@mui/material/SvgIcon';
import { ThemeConsumer } from 'styled-components';
import { HighlightedSatelliteInFov } from '../../TabbedPanel/TerminalTabPanel';
import { TerminalDetails } from '../../../../../shared/entities/terminal';
import SatelliteIndicator from './SatelliteIndicator';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { FEATURE_FLAGS, isFeatureEnabled } from '../../../../../utils/FeatureFlagUtils';
import {
  getHighestPriorityConnectivityStatus,
  getConnectivityColor,
  getConnectivityIntPriority,
} from '../../../../../utils/ConnectionStatusUtils';
import { RootState } from '../../../../../store';
import { connect } from 'react-redux';
import { isChristmasTime } from '../../../../../utils/SeasonalFun';

export interface LookAnglesCompassProps extends StateProps {
  terminalDetails: TerminalDetails;
  isOverviewMode: boolean;
  satellitesInFOVIndexOffset: number;
  highlightedSatelliteInFov: HighlightedSatelliteInFov | undefined;
  setHighlightedSatelliteInFov: Function;
}

interface SatelliteRenderOrderProps {
  satelliteArrayIndex: number;
  connectivityPriority: number;
}

function LookAnglesCompass({
  terminalDetails,
  isOverviewMode,
  satellitesInFOVIndexOffset,
  highlightedSatelliteInFov,
  setHighlightedSatelliteInFov,
  appConfig,
}: LookAnglesCompassProps) {
  const [noHeadingTooltipVisible, setNoHeadingTooltipVisible] = useState(false);

  const showNoHeadingTooltip = (terminalDetails: TerminalDetails) => {
    if (!isNil(terminalDetails) && isNil(terminalDetails.headingDeg)) {
      setNoHeadingTooltipVisible(true);
    }
  };
  const hideNoHeadingTooltip = () => {
    setNoHeadingTooltipVisible(false);
  };

  const handleClickAway = () => {
    if (
      highlightedSatelliteInFov &&
      highlightedSatelliteInFov.sticky &&
      highlightedSatelliteInFov.setByGraphicIndicator
    ) {
      setHighlightedSatelliteInFov(undefined);
    }
  };

  const isHiglighted = (satelliteIdx: number) => {
    return highlightedIdx !== -1 ? highlightedIdx === satelliteIdx : true;
  };

  const getAirborneIconSvg = useCallback((terminalDetails: TerminalDetails) => {
    if (isChristmasTime(appConfig)) {
      let transform = '';
      if (!isNil(terminalDetails) && !isNil(terminalDetails.headingDeg)) {
        transform = `rotate(${terminalDetails.headingDeg + 90},600,600)`;
        if (terminalDetails.headingDeg < 180) {
          transform += ' translate(600,600) scale(1,-1) translate(-600,-600)'; // Mirror the icon when heading right
        }
      }
      return (
        <SvgIcon width="1200" height="1200" x="0" y="0" fill="white" viewBox="0 0 42 42" transform={transform}>
          <SleighIcon />
        </SvgIcon>
      );
    } else {
      return (
        <svg
          width="1000"
          height="1000"
          x="100"
          y="100"
          viewBox="0 0 1000 1000"
          xmlns="http://www.w3.org/2000/svg"
          onMouseEnter={() => {
            showNoHeadingTooltip(terminalDetails);
          }}
          onMouseLeave={() => {
            hideNoHeadingTooltip();
          }}
        >
          <svg
            width="880"
            height="880"
            viewBox="0 0 1000 1000"
            xmlns="http://www.w3.org/2000/svg"
            onMouseEnter={() => {
              showNoHeadingTooltip(terminalDetails);
            }}
            onMouseLeave={() => {
              hideNoHeadingTooltip();
            }}
          >
            <svg
              onMouseEnter={() => {
                showNoHeadingTooltip(terminalDetails);
              }}
              onMouseLeave={() => {
                hideNoHeadingTooltip();
              }}
              xmlns="http://www.w3.org/2000/svg"
              width="600"
              height="600"
              x="260"
              y="260"
              viewBox="0  0 30 30"
              fill="white"
            >
              {!isNil(terminalDetails) && !isNil(terminalDetails.headingDeg) ? (
                <g transform={`rotate(${terminalDetails.headingDeg}, 15, 15)`}>
                  <g transform="translate(3.5, 3.5)">
                    <path d="M10.18 9" />
                    <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z" />
                  </g>
                </g>
              ) : null}
            </svg>
          </svg>
          {!isNil(terminalDetails) && !isNil(terminalDetails.headingDeg) ? null : (
            <g>
              <circle cx="500" cy="500" r="120" fill="#677a89" />
              <circle cx="500" cy="500" r="64" fill="#c3cdd5" />
            </g>
          )}
        </svg>
      );
    }
  }, [appConfig]);

  let className = 'compass__container';

  const isSatellitesInFOVEnabled = isFeatureEnabled(appConfig, FEATURE_FLAGS.isSatellitesInFOVEnabled);
  let satellites = getTerminalSatelliteList(terminalDetails, isOverviewMode, isSatellitesInFOVEnabled);
  let highlightedIdx = satellites.findIndex(
    (satellite: SatelliteInFov) => satellite.satelliteName === (highlightedSatelliteInFov || {}).satelliteName,
  );
  let isStickyMode = highlightedSatelliteInFov && highlightedSatelliteInFov.sticky;

  // Determine the rendering order of the satellites
  // where the highlighted satellite is rendered last
  let satelliteRenderOrder = satellites
    .map((satellite: SatelliteInFov, index: number) => {
      return {
        satelliteArrayIndex: index,
        connectivityPriority: getConnectivityIntPriority(satellite.connectivityStatus),
      } as SatelliteRenderOrderProps;
    })
    .filter((satellite: SatelliteRenderOrderProps) => satellite.satelliteArrayIndex !== highlightedIdx)
    .reverse()
    .sort((a, b) => a.connectivityPriority - b.connectivityPriority)
    .map((satellite: SatelliteRenderOrderProps) => satellite.satelliteArrayIndex)
    .concat(highlightedIdx === -1 ? [] : [highlightedIdx]);

  let beamConnectionStatuses = satellites
    .filter((s: SatelliteInFov) => !s.isAlternative && !isNil(s.azimuthDeg))
    .map((s: SatelliteInFov) => s.connectivityStatus);

  if (
    !isNil(terminalDetails) &&
    isPlatformTypeInList(terminalDetails.platformType, TerminalPlatformsEnum.GroundNomadic)
  ) {
    className = className + ' compass__container--ground_terminal';
  }

  return (
    <ThemeConsumer>
      {(theme: ThemeInterface) => (
        <div className={className}>
          <span className="north__label">N</span>
          <div onClick={handleClickAway}>
            <ClickAwayListener onClickAway={handleClickAway}>
              <div className="look__angles__dynamics__container">
                <svg
                  className="look__angles__current__look_angle__graphic"
                  width="1200"
                  height="1200"
                  viewBox="0 0 1200 1200"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle className="look__angles__compass__circle" cx="600" cy="600" r="500" fill="#2B343A" />
                  <svg
                    onMouseEnter={() => {
                      showNoHeadingTooltip(terminalDetails);
                    }}
                    onMouseLeave={() => {
                      hideNoHeadingTooltip();
                    }}
                    className="look__angles__compass__tick__marks"
                    width="1000"
                    height="1000"
                    x="100"
                    y="100"
                    viewBox="0 0 1000 1000"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <line x1="60" y1="500" x2="940" y2="500" strokeWidth="16" stroke="white" />
                    <line
                      transform="rotate(90, 500, 500)"
                      x1="60"
                      y1="500"
                      x2="940"
                      y2="500"
                      strokeWidth="16"
                      stroke="white"
                    />
                    <line
                      transform="rotate(45, 500, 500)"
                      x1="60"
                      y1="500"
                      x2="940"
                      y2="500"
                      strokeWidth="16"
                      stroke="white"
                    />
                    <line
                      transform="rotate(22.5, 500, 500)"
                      x1="60"
                      y1="500"
                      x2="940"
                      y2="500"
                      strokeWidth="16"
                      stroke="white"
                    />
                    <line
                      transform="rotate(-45, 500, 500)"
                      x1="60"
                      y1="500"
                      x2="940"
                      y2="500"
                      strokeWidth="16"
                      stroke="white"
                    />
                    <line
                      transform="rotate(-67.5, 500, 500)"
                      x1="60"
                      y1="500"
                      x2="940"
                      y2="500"
                      strokeWidth="16"
                      stroke="white"
                    />
                    <line
                      transform="rotate(67.5, 500, 500)"
                      x1="60"
                      y1="500"
                      x2="940"
                      y2="500"
                      strokeWidth="16"
                      stroke="white"
                    />
                    <line
                      transform="rotate(-22.5, 500, 500)"
                      x1="60"
                      y1="500"
                      x2="940"
                      y2="500"
                      strokeWidth="16"
                      stroke="white"
                    />
                    <circle cx="500" cy="500" r="410" fill="#2B343A" />
                  </svg>

                  <line
                    className="look__angles__compass__north__line"
                    x1="600"
                    y1="40"
                    x2="600"
                    y2="190"
                    strokeWidth="16"
                    stroke="white"
                  />
                  {!isNil(terminalDetails) &&
                    isPlatformTypeInList(terminalDetails.platformType, TerminalPlatformsEnum.GroundNomadic) ? (
                    <SvgIcon width="360" height="360" x="428" y="412" fill="white" viewBox="0 0 42 42">
                      <SvgGroundTerminalIconPath />
                    </SvgIcon>
                  ) : (
                    getAirborneIconSvg(terminalDetails)
                  )}
                  {satellites && satellites.length > 0
                    ? satelliteRenderOrder.map((index: number) => (
                      <React.Fragment key={satellites[index].satelliteName}>
                        {satellites[index] ? (
                          <SatelliteIndicator
                            satelliteInFov={satellites[index]}
                            index={index + satellitesInFOVIndexOffset}
                            isHighlighted={isHiglighted(index)}
                            isStickyMode={isStickyMode}
                            setHighlightedSatelliteInFov={setHighlightedSatelliteInFov}
                          />
                        ) : null}
                      </React.Fragment>
                    ))
                    : null}
                  <svg
                    width="1000"
                    height="1000"
                    x="100"
                    y="100"
                    viewBox="0 0 1000 1000"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    {beamConnectionStatuses.length > 0 ? (
                      <circle
                        cx="500"
                        cy="500"
                        r="20"
                        fill={getConnectivityColor(getHighestPriorityConnectivityStatus(...beamConnectionStatuses))}
                      />
                    ) : null}
                  </svg>
                </svg>
              </div>
            </ClickAwayListener>
            {noHeadingTooltipVisible &&
              isPlatformTypeInList(
                terminalDetails.platformType,
                TerminalPlatformsEnum.Airborne,
                TerminalPlatformsEnum.Maritime,
              ) ? (
              <span className="heading__unknown__tooltip">Heading unknown</span>
            ) : null}
          </div>
        </div>
      )}
    </ThemeConsumer>
  );
}

const mapStateToProps = (state: RootState) => ({
  appConfig: state.config.appConfig,
});
type StateProps = ReturnType<typeof mapStateToProps>;

export default connect(mapStateToProps)(LookAnglesCompass);
